import { IfcClass, RevitCategory } from './geometry/algorithm-map';

export enum SparkelCategory {
  Wall = 'Walls',
  Window = 'Windows',
  Door = 'Doors',
  Slab = 'Slabs',
  Column = 'Columns',
  Beam = 'Beams',
  Space = 'Spaces',
  Roof = 'Roofs',
  CurtainWall = 'Curtain Walls',
  Covering = 'Coverings',
  Member = 'Members',
  Proxy = 'Proxy',
  Part = 'Parts',
  Footing = 'Footings',
  Pile = 'Piles',
  PipeSegment = 'Pipe Segments',
  PipeFitting = 'Pipe Fittings',
  ElementAssembly = 'Element Assemblies',
  Furniture = 'Furniture',
  Plants = 'Planting',
  Stairs = 'Stairs',
  Railing = 'Railings',
  Other = 'Other',
}

/**
 * Maps Sparkel categories to IFC classes and Revit categories
 * This is the reverse mapping of getSparkelCategoryFromIfcClass and getSparkelCategoryFromRevitCategory
 */
export const SPARKEL_TO_CATEGORIES_MAP: Record<
  SparkelCategory,
  { ifcClasses: string[]; revitCategories: string[] }
> = {
  [SparkelCategory.Wall]: {
    ifcClasses: [IfcClass.Wall, IfcClass.WallStandardCase],
    revitCategories: [RevitCategory.Wall],
  },
  [SparkelCategory.Window]: {
    ifcClasses: [IfcClass.Window],
    revitCategories: [RevitCategory.Window],
  },
  [SparkelCategory.Door]: {
    ifcClasses: [IfcClass.Door],
    revitCategories: [RevitCategory.Door],
  },
  [SparkelCategory.Slab]: {
    ifcClasses: [IfcClass.Slab],
    revitCategories: [RevitCategory.Slab],
  },
  [SparkelCategory.Column]: {
    ifcClasses: [IfcClass.Column],
    revitCategories: [RevitCategory.Column, RevitCategory.StructuralColumn],
  },
  [SparkelCategory.Beam]: {
    ifcClasses: [IfcClass.Beam],
    revitCategories: [RevitCategory.Beam, RevitCategory.StructuralBeam],
  },
  [SparkelCategory.Space]: {
    ifcClasses: [IfcClass.Space],
    revitCategories: [RevitCategory.Space],
  },
  [SparkelCategory.Roof]: {
    ifcClasses: [IfcClass.Roof],
    revitCategories: [RevitCategory.Roof],
  },
  [SparkelCategory.CurtainWall]: {
    ifcClasses: [IfcClass.CurtainWall],
    revitCategories: [RevitCategory.CurtainWall],
  },
  [SparkelCategory.Covering]: {
    ifcClasses: [IfcClass.Covering],
    revitCategories: [
      RevitCategory.Covering,
      RevitCategory.Ceiling,
      'Revit Covers',
      'Revit Ceilings',
    ],
  },
  [SparkelCategory.Member]: {
    ifcClasses: [IfcClass.Member],
    revitCategories: [
      RevitCategory.Member,
      RevitCategory.StructuralMember,
      RevitCategory.StructuralFraming,
    ],
  },
  [SparkelCategory.Proxy]: {
    ifcClasses: [IfcClass.Proxy],
    revitCategories: [],
  },
  [SparkelCategory.Part]: {
    ifcClasses: [IfcClass.Part],
    revitCategories: [RevitCategory.Parts],
  },
  [SparkelCategory.Footing]: {
    ifcClasses: [IfcClass.Footing],
    revitCategories: [],
  },
  [SparkelCategory.Pile]: {
    ifcClasses: [IfcClass.Pile],
    revitCategories: [],
  },
  [SparkelCategory.PipeSegment]: {
    ifcClasses: [IfcClass.PipeSegment],
    revitCategories: [],
  },
  [SparkelCategory.PipeFitting]: {
    ifcClasses: [IfcClass.PipeFitting],
    revitCategories: [],
  },
  [SparkelCategory.ElementAssembly]: {
    ifcClasses: [IfcClass.ElementAssembly],
    revitCategories: [],
  },
  [SparkelCategory.Furniture]: {
    ifcClasses: [],
    revitCategories: [RevitCategory.Furniture],
  },
  [SparkelCategory.Plants]: {
    ifcClasses: [],
    revitCategories: [RevitCategory.Plants],
  },
  [SparkelCategory.Stairs]: {
    ifcClasses: [],
    revitCategories: [RevitCategory.Stairs],
  },
  [SparkelCategory.Railing]: {
    ifcClasses: [],
    revitCategories: [RevitCategory.Railing],
  },
  [SparkelCategory.Other]: {
    ifcClasses: [],
    revitCategories: [],
  },
};

/**
 * Get all IFC classes and Revit categories for a list of Sparkel categories
 */
export function getCategoriesFromSparkelCategories(
  sparkelCategories: SparkelCategory[]
): string[] {
  const categories: string[] = [];

  for (const sparkelCategory of sparkelCategories) {
    const mapping = SPARKEL_TO_CATEGORIES_MAP[sparkelCategory];
    if (mapping) {
      categories.push(...mapping.ifcClasses, ...mapping.revitCategories);
    }
  }

  return categories;
}

/**
 * Get Sparkel category from IFC class
 * This is the forward mapping (IFC -> Sparkel)
 */
const IFC_TO_SPARKEL_MAP: Record<string, SparkelCategory> = {
  [IfcClass.Wall]: SparkelCategory.Wall,
  [IfcClass.WallStandardCase]: SparkelCategory.Wall,
  [IfcClass.Window]: SparkelCategory.Window,
  [IfcClass.Door]: SparkelCategory.Door,
  [IfcClass.Slab]: SparkelCategory.Slab,
  [IfcClass.Column]: SparkelCategory.Column,
  [IfcClass.Beam]: SparkelCategory.Beam,
  [IfcClass.Space]: SparkelCategory.Space,
  [IfcClass.Roof]: SparkelCategory.Roof,
  [IfcClass.CurtainWall]: SparkelCategory.CurtainWall,
  [IfcClass.Covering]: SparkelCategory.Covering,
  [IfcClass.Member]: SparkelCategory.Member,
  [IfcClass.Proxy]: SparkelCategory.Proxy,
  [IfcClass.Part]: SparkelCategory.Part,
  [IfcClass.Footing]: SparkelCategory.Footing,
  [IfcClass.Pile]: SparkelCategory.Pile,
  [IfcClass.PipeSegment]: SparkelCategory.PipeSegment,
  [IfcClass.PipeFitting]: SparkelCategory.PipeFitting,
  [IfcClass.ElementAssembly]: SparkelCategory.ElementAssembly,
};

export function getSparkelCategoryFromIfcClass(
  ifcClass: string
): SparkelCategory {
  return IFC_TO_SPARKEL_MAP[ifcClass] ?? SparkelCategory.Other;
}

/**
 * Get Sparkel category from Revit category
 * This is the forward mapping (Revit -> Sparkel)
 */
// Helper function to build the map since we can't have duplicate keys in object literals
function buildRevitToSparkelMap(): Record<string, SparkelCategory> {
  const map: Record<string, SparkelCategory> = {
    [RevitCategory.Wall]: SparkelCategory.Wall,
    [RevitCategory.Window]: SparkelCategory.Window,
    [RevitCategory.Door]: SparkelCategory.Door,
    [RevitCategory.Slab]: SparkelCategory.Slab,
    [RevitCategory.Column]: SparkelCategory.Column,
    [RevitCategory.StructuralColumn]: SparkelCategory.Column,
    [RevitCategory.Beam]: SparkelCategory.Beam,
    [RevitCategory.StructuralBeam]: SparkelCategory.Beam,
    [RevitCategory.Space]: SparkelCategory.Space,
    [RevitCategory.Roof]: SparkelCategory.Roof,
    [RevitCategory.CurtainWall]: SparkelCategory.CurtainWall,
    [RevitCategory.Covering]: SparkelCategory.Covering,
    [RevitCategory.Ceiling]: SparkelCategory.Covering,
    [RevitCategory.Member]: SparkelCategory.Member,
    [RevitCategory.StructuralMember]: SparkelCategory.Member,
    [RevitCategory.StructuralFraming]: SparkelCategory.Member,
    [RevitCategory.Furniture]: SparkelCategory.Furniture,
    [RevitCategory.Plants]: SparkelCategory.Plants,
    [RevitCategory.Stairs]: SparkelCategory.Stairs,
    [RevitCategory.Railing]: SparkelCategory.Railing,
  };
  // Add string literals that might not be in the enum
  map['Revit Covers'] = SparkelCategory.Covering;
  map['Revit Ceilings'] = SparkelCategory.Covering;
  return map;
}

const REVIT_TO_SPARKEL_MAP = buildRevitToSparkelMap();

export function getSparkelCategoryFromRevitCategory(
  revitCategory: string
): SparkelCategory {
  return REVIT_TO_SPARKEL_MAP[revitCategory] ?? SparkelCategory.Other;
}

/**
 * Get all available Sparkel categories (excluding 'Other')
 */
export function getAllSparkelCategories(): SparkelCategory[] {
  return Object.values(SparkelCategory)
    .filter((cat) => cat !== SparkelCategory.Other)
    .sort();
}
